import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import {
  criarUsuario,
  loginUsuario,
  logoutUsuario,
  observarEstadoAutenticacao,
} from '../api/auth.api';
import { IUsuario as Usuario } from '../types/firestore.types';
import Spinner from '../components/ui/Spinner';
import Alert from '../components/ui/Alert';
import { criarCliente } from '@/api/clients.api';
import { User, sendEmailVerification, reload, sendPasswordResetEmail } from 'firebase/auth';
import { auth } from '@/config/firebase.config';

interface AuthContextType {
  usuario: Usuario | null;
  usuarioFirebase: User | null; // ✅ ADICIONADO: Usuário do Firebase
  login: (email: string, senha: string) => Promise<void>;
  cadastro: (email: string, senha: string, nome: string, telefone: string | null, tipo_usuario: string, unidade_id?: string | null, aceitou_termos?: boolean) => Promise<void>; 
  logout: () => Promise<void>;
  resetarSenha: (email: string) => Promise<void>;
  carregandoAutenticacao: boolean;
  erroAutenticacao: string | null;
  limparErro: () => void;
  reenviarEmailValidacao: () => Promise<void>; // ✅ ADICIONADO
  recarregarUsuarioFirebase: () => Promise<void>; // ✅ ADICIONADO
}

export const AuthContext = createContext<AuthContextType | null>(null);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [usuario, setUsuario] = useState<Usuario | null>(null);
  const [usuarioFirebase, setUsuarioFirebase] = useState<User | null>(null); // ✅ NOVO ESTADO
  const [carregandoAutenticacao, setCarregandoAutenticacao] = useState(true);
  const [erroAutenticacao, setErroAutenticacao] = useState<string | null>(null);

  const limparErro = useCallback(() => {
    setErroAutenticacao(null);
  }, []);

  useEffect(() => {
    const unsubscribe = observarEstadoAutenticacao((user) => {
      setUsuario(user);
      setUsuarioFirebase(auth.currentUser); // ✅ ARMAZENA O USUÁRIO DO FIREBASE
      setCarregandoAutenticacao(false);
    });
    return () => unsubscribe();
  }, []);

  // ✅ NOVA FUNÇÃO PARA REENVIAR EMAIL DE VERIFICAÇÃO
  const handleReenviarEmailValidacao = useCallback(async () => {
    if (!usuarioFirebase) {
      throw new Error('Usuário não autenticado');
    }

    try {
      await sendEmailVerification(usuarioFirebase);
    } catch (error: any) {
      console.error('Erro ao reenviar email de verificação:', error);
      throw error;
    }
  }, [usuarioFirebase]);

  const handleResetarSenha = useCallback(async (email: string) => {
    setErroAutenticacao(null);

    try {
      // Use a função do Firebase para resetar senha
      await sendPasswordResetEmail(auth, email);

      // Log adicional para debugging

    } catch (error: any) {
      console.error('❌ Erro ao enviar email:', error);
      console.error('Código do erro:', error.code);
      console.error('Mensagem completa:', error.message);

      // ✅ TRATAMENTO ESPECÍFICO DE ERROS DO FIREBASE
      let mensagemErro = 'Falha ao enviar email de recuperação.';

      switch (error.code) {
        case 'auth/user-not-found':
          mensagemErro = 'Nenhum usuário encontrado com este email.';
          break;
        case 'auth/invalid-email':
          mensagemErro = 'Email inválido.';
          break;
        case 'auth/too-many-requests':
          mensagemErro = 'Muitas tentativas. Tente novamente mais tarde.';
          break;
        case 'auth/network-request-failed':
          mensagemErro = 'Erro de conexão. Verifique sua internet.';
          break;
        default:
          mensagemErro = error.message || mensagemErro;
      }

      setErroAutenticacao(mensagemErro);
      throw new Error(mensagemErro);
    }
  }, []);

  // ✅ NOVA FUNÇÃO PARA RECARREGAR USUÁRIO DO FIREBASE
  const handleRecarregarUsuarioFirebase = useCallback(async () => {
    if (!usuarioFirebase) {
      throw new Error('Usuário não autenticado');
    }

    try {
      await reload(usuarioFirebase);
      // Atualiza o estado com o usuário recarregado
      setUsuarioFirebase(auth.currentUser);
    } catch (error: any) {
      console.error('Erro ao recarregar usuário:', error);
      throw error;
    }
  }, [usuarioFirebase]);

  const handleLogin = useCallback(async (email: string, senha: string) => {
    setCarregandoAutenticacao(true);
    setErroAutenticacao(null);
    try {
      const user = await loginUsuario(email, senha);
      setUsuario(user);
      setUsuarioFirebase(auth.currentUser); // ✅ ATUALIZA USUÁRIO FIREBASE
    } catch (error: any) {
      console.error('Erro ao fazer login:', error);
      setErroAutenticacao(error.message || 'Falha ao fazer login. Verifique suas credenciais.');
      setCarregandoAutenticacao(false);
      throw error;
    } finally {
      setCarregandoAutenticacao(false);
    }
  }, []);



  // ✅ CORRIGIDO: FUNÇÃO DE CADASTRO SIMPLIFICADA
  const handleCadastro = useCallback(async (
    email: string,
    senha: string,
    nome: string,
    telefone: string,
    tipo_usuario: Usuario['tipo_usuario'],
    unidade_id?: string,
    aceitou_termos: boolean = true
  ) => {
    setCarregandoAutenticacao(true);
    setErroAutenticacao(null);
    try {
      const user = await criarUsuario(
        email,
        senha,
        nome,
        telefone,
        tipo_usuario,
        unidade_id,
        aceitou_termos
      );

      if (tipo_usuario === 'cliente') {
        user.telefone = telefone;
        setUsuario(user);
        setUsuarioFirebase(auth.currentUser); // ✅ ATUALIZA USUÁRIO FIREBASE
      }

      // O Firebase automaticamente envia email de verificação
      // quando um novo usuário é criado

    } catch (error: any) {
      console.error('Erro ao cadastrar usuário:', error);
      setErroAutenticacao(error.message || 'Falha ao cadastrar. Tente novamente.');
      setCarregandoAutenticacao(false);
      throw error;
    } finally {
      setCarregandoAutenticacao(false);
    }
  }, []);

  const handleLogout = useCallback(async () => {
    setCarregandoAutenticacao(true);
    setErroAutenticacao(null);
    try {
      await logoutUsuario();
      setUsuario(null);
      setUsuarioFirebase(null); // ✅ LIMPA USUÁRIO FIREBASE
    } catch (error: any) {
      console.error('Erro ao fazer logout:', error);
      setErroAutenticacao(error.message || 'Falha ao sair. Tente novamente.');
      throw error;
    } finally {
      setCarregandoAutenticacao(false);
    }
  }, []);

  const value = {
    usuario,
    usuarioFirebase, // ✅ ADICIONADO AO CONTEXTO
    login: handleLogin,
    cadastro: handleCadastro,
    logout: handleLogout,
    resetarSenha: handleResetarSenha,
    carregandoAutenticacao,
    erroAutenticacao,
    limparErro,
    reenviarEmailValidacao: handleReenviarEmailValidacao, // ✅ ADICIONADO
    recarregarUsuarioFirebase: handleRecarregarUsuarioFirebase, // ✅ ADICIONADO
  };

  if (carregandoAutenticacao) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <Spinner size="lg" color="primary" />
      </div>
    );
  }

  return (
    <AuthContext.Provider value={value}>
      {erroAutenticacao && (
        <div className="fixed top-4 right-4 z-50">
          <Alert tipo="error" mensagem={erroAutenticacao} onClose={limparErro} />
        </div>
      )}
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth deve ser utilizado dentro de um AuthProvider');
  }
  return context;
};